// imports
import { FC } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, CircularProgress } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

// components
import { Alert } from 'components/common/Alert';
import InputController from 'controller/InputController';

// constants, helper, graphql, contexts
import { VerifyOTPInput } from 'interfaces';
import { useAuthContext } from 'contexts/AuthContext';
import { verifyOTPSchema } from 'validationSchema';
import { useVerifyCodeMutation } from 'generated/graphql';
import {
  TOKEN,
  OTP_TEXT,
  AUTH_LINKS,
  HTTP_STATUS,
  VERIFY_TEXT,
  DASHBOARD_ROUTE,
  TOKEN_QUERY_TEXT,
  INVALID_TOKEN_TEXT,
  VerifyOTPInitialValues,
  CANCEL_TEXT,
} from 'constants/index';

const VerifyOTP: FC = () => {
  const navigate = useNavigate();
  const { setIsLoggedIn } = useAuthContext();
  const [searchParams] = useSearchParams();
  const token = searchParams.get(TOKEN_QUERY_TEXT);
  const methods = useForm<VerifyOTPInput>({
    resolver: yupResolver(verifyOTPSchema),
    defaultValues: VerifyOTPInitialValues,
  });

  const { handleSubmit } = methods;

  const [verifyCode, { loading }] = useVerifyCodeMutation({
    onCompleted: (data) => {
      const { verifyCode } = data || {};
      const { response, token: newToken } = verifyCode || {};
      const { status, message } = response || {};

      if (status === HTTP_STATUS.SUCCESS && newToken) {
        setIsLoggedIn(true);
        localStorage.setItem(TOKEN, newToken);
        navigate(DASHBOARD_ROUTE);
      } else {
        Alert.error(message ?? '');
      }
    },
    onError: ({ message }) => {
      return Alert.error(message);
    },
  });

  const onSubmit: SubmitHandler<VerifyOTPInput> = async (values) => {
    if (!token) {
      return Alert.error(INVALID_TOKEN_TEXT);
    }
    await verifyCode({
      variables: {
        verifyCodeInput: {
          token: token,
          code: Number(values.otp),
          rememberMe: false,
        },
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} data-test="otp-form">
        <Box data-test="otp-input">
          <InputController isRequired name={'otp'} title={OTP_TEXT} fieldType={'number'} disabled={loading} />
        </Box>
        <Box marginTop={2} display="flex" alignItems="center" gap={1}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate(AUTH_LINKS.LOGIN_LINK, { replace: true })}
            data-test="cancel-btn"
            disabled={loading}>
            {CANCEL_TEXT}
          </Button>
          <Button
            variant="contained"
            type="submit"
            data-test="verify-otp-btn"
            fullWidth
            color="primary"
            disabled={loading}
            endIcon={loading && <CircularProgress size={20} color="inherit" />}>
            {VERIFY_TEXT}
          </Button>
        </Box>
      </form>
    </FormProvider>
  );
};

export default VerifyOTP;
